import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Header from '../components/Header';
import AdScript from '../components/AdScript';

export default function HomePage() {
  const location = useLocation();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);

  useEffect(() => {
    fetchTrips();
  }, [location.pathname]);

  const handleMenuClick = (e, tripId) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenMenuId(openMenuId === tripId ? null : tripId);
  };

  const handleClickOutside = () => {
    setOpenMenuId(null);
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const fetchTrips = async () => {
    try {
      const { data: tripsData, error: tripsError } = await supabase
        .from('trips')
        .select('*')
        .order('created_at', { ascending: false });

      if (tripsError) throw tripsError;

      const tripsWithDestinations = await Promise.all(
        tripsData.map(async (trip) => {
          const { data: destinations, error: destError } = await supabase
            .from('destinations')
            .select('*')
            .eq('trip_id', trip.id)
            .order('order', { ascending: true })
            .order('created_at', { ascending: true });

          if (destError) throw destError;

          return {
            ...trip,
            destinations: destinations || [],
          };
        })
      );

      setTrips(tripsWithDestinations);
    } catch (error) {
      console.error('Error fetching trips:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleDeleteTrip = async (tripId) => {
    setTripToDelete(tripId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const { data: expenses, error: expensesQueryError } = await supabase
        .from('expense_groups')
        .select('id')
        .eq('trip_id', tripToDelete);

      if (expensesQueryError) throw expensesQueryError;

      if (expenses?.length > 0) {
        const groupIds = expenses.map(group => group.id);
        
        const { error: expensesDeleteError } = await supabase
          .from('expenses_table')
          .delete()
          .in('group_id', groupIds);

        if (expensesDeleteError) throw expensesDeleteError;
      }

      const { error: groupsError } = await supabase
        .from('expense_groups')
        .delete()
        .eq('trip_id', tripToDelete);

      if (groupsError) throw groupsError;

      const { error: destError } = await supabase
        .from('destinations')
        .delete()
        .eq('trip_id', tripToDelete);

      if (destError) throw destError;

      const { error: tripError } = await supabase
        .from('trips')
        .delete()
        .eq('id', tripToDelete);

      if (tripError) throw tripError;

      fetchTrips();
    } catch (error) {
      console.error('Error deleting trip:', error);
      alert('Failed to delete trip. Please try again.');
    } finally {
      setShowDeleteConfirm(false);
      setTripToDelete(null);
    }
  };

  const deleteConfirmationDialog = (
    <div className={`fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity ${showDeleteConfirm ? 'flex' : 'hidden'} z-50`}>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-base font-semibold leading-6 text-gray-900">Delete Trip</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this trip? This will also delete all related expenses and expense groups. This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                onClick={confirmDelete}
                className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setTripToDelete(null);
                }}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cover bg-fixed bg-center" 
      style={{ 
        backgroundImage: 'url("https://images.unsplash.com/photo-1506197603052-3cc9c3a201bd?q=80&w=2070&auto=format&fit=crop")',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backgroundBlend: 'overlay'
      }}>
      {deleteConfirmationDialog}
      <Header />
      <main>
        <main className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">My Trips</h1>
            <Link
              to="/trips/new"
              className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-md transition-all duration-200"
            >
              Create New Trip
            </Link>
          </div>

          {loading ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : trips.length === 0 ? (
            <div className="text-center py-12 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg">
              <p className="text-gray-800 text-xl font-medium">No trips yet. Create your first trip!</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {trips.map((trip) => (
                <Link
                  key={trip.id}
                  to={`/trips/${trip.id}`}
                  className="block bg-white/90 backdrop-blur-sm rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start">
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">{trip.title}</h2>
                      <div className="relative">
                        <button
                          onClick={(e) => handleMenuClick(e, trip.id)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                          </svg>
                        </button>
                        {openMenuId === trip.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                            <Link
                              to={`/trips/${trip.id}/edit`}
                              onClick={(e) => e.stopPropagation()}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Edit trip
                            </Link>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteTrip(trip.id);
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                            >
                              Delete trip
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-sm font-medium text-gray-600 mb-4">
                      {formatDate(trip.start_date)} - {formatDate(trip.end_date)}
                    </div>
                    <div className="text-sm font-medium text-gray-800">
                      {trip.destinations.length} destinations
                    </div>
                    {trip.destinations.length > 0 && (
                      <div className="text-sm font-medium text-gray-800 mt-2">
                        Starting from: {trip.destinations[0].name}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </main>
      </main>
      <AdScript />
    </div>
  );
}
